import { useEffect, useCallback } from 'react';
import { useGameRealTimeUpdates } from './useGameRealTimeUpdates';
import { useRealTimeGameUpdates } from './useRealTimeGameUpdates';
import { useEnhancedGameNotifications } from '@/components/games/EnhancedGameNotifications';

interface UseGameRealTimeManagerProps {
  userId?: string;
  gameId?: string;
  onGameUpdate?: () => void;
  onRosterUpdate?: () => void;
  onPlayerStatusUpdate?: () => void;
  onConfirmationUpdate?: () => void;
  onGameStatusUpdate?: () => void;
}

export const useGameRealTimeManager = ({
  userId,
  gameId,
  onGameUpdate,
  onRosterUpdate,
  onPlayerStatusUpdate,
  onConfirmationUpdate,
  onGameStatusUpdate
}: UseGameRealTimeManagerProps) => {
  
  // Enhanced game notifications
  useEnhancedGameNotifications({
    userId,
    onNotification: useCallback((type: string, data: any) => {
      console.log('Game notification received:', { type, data });
      
      // Route notifications to appropriate handlers
      switch (type) {
        case 'game_update':
          onGameUpdate?.();
          break;
        case 'roster_update':
          onRosterUpdate?.();
          break;
        case 'player_status':
          onPlayerStatusUpdate?.();
          break;
        case 'confirmation_update':
          onConfirmationUpdate?.();
          break;
        case 'game_status':
          onGameStatusUpdate?.();
          break;
      }
    }, [onGameUpdate, onRosterUpdate, onPlayerStatusUpdate, onConfirmationUpdate, onGameStatusUpdate])
  });

  // Periodic game status update
  const updateGameStatuses = useCallback(async () => {
    try {
      console.log('🎮 Checking for games that need status updates...');
      
      const response = await fetch('https://gmmfphwrwhvixldazcmp.supabase.co/functions/v1/update-game-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('❌ HTTP error updating game statuses:', response.status, response.statusText);
        return;
      }
      
      const result = await response.json();
      
      if (result.error) {
        console.error('❌ Error updating game statuses:', result.error);
        return;
      }

      const data = result.data;
      
      if (data?.updatedVenueGames?.length > 0 || data?.updatedTodayGames?.length > 0) {
        console.log('✅ Games updated to in_progress:', data);
        // Trigger callbacks to refresh data
        onGameUpdate?.();
        onGameStatusUpdate?.();
      }
    } catch (error) {
      console.error('❌ Failed to update game statuses:', error);
    }
  }, [onGameUpdate, onGameStatusUpdate]);

  // Set up periodic status checks every 2 minutes
  useEffect(() => {
    // Run immediately
    updateGameStatuses();
    
    // Then run every 2 minutes
    const interval = setInterval(updateGameStatuses, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [updateGameStatuses]);

  // Game-specific real-time updates
  useGameRealTimeUpdates({
    gameId,
    userId,
    onGameUpdate,
    onRosterUpdate,
    onPlayerStatusUpdate,
    onConfirmationUpdate
  });

  // General game status updates
  useRealTimeGameUpdates({
    userId,
    onGameStatusUpdate
  });

  // Cleanup function
  const cleanup = useCallback(() => {
    // This will be handled by the individual hooks
    console.log('Cleaning up game real-time subscriptions');
  }, []);

  // Return cleanup function for manual cleanup if needed
  return {
    cleanup
  };
};